import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RequestInputRequiredContent } from "@/services/types/agent-gateway";
import { CircleCheck, CircleHelp } from "lucide-react";
import { useMemo, useState } from "react";

type JsonSchema = Record<string, unknown>;

type RequestInputCardProps = {
  request: RequestInputRequiredContent;
  answered?: boolean;
  answeredResponse?: unknown;
  disabled?: boolean;
  errorMessage?: string;
  isPending?: boolean;
  onSubmit: (response: unknown) => void;
};

function schemaType(schema: JsonSchema | null) {
  return typeof schema?.type === "string" ? schema.type : "string";
}

function schemaEnum(schema: JsonSchema | null) {
  return Array.isArray(schema?.enum)
    ? schema.enum.filter(
        (value): value is string =>
          typeof value === "string" && value.length > 0,
      )
    : [];
}

function formatSchemaChoice(value: string, schema: JsonSchema | null) {
  const labels = schema?.["x-enum-labels"];
  if (
    typeof labels === "object" &&
    labels !== null &&
    !Array.isArray(labels) &&
    typeof (labels as Record<string, unknown>)[value] === "string"
  ) {
    return (labels as Record<string, string>)[value];
  }
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function coerceValue(value: string | boolean, schema: JsonSchema) {
  if (schema.type === "boolean") {
    return Boolean(value);
  }
  if (schema.type === "integer") {
    return Number.parseInt(String(value), 10);
  }
  if (schema.type === "number") {
    return Number(String(value));
  }
  return String(value);
}

function displayValue(value: unknown) {
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value, null, 2);
}

export function RequestInputCard({
  request,
  answered = false,
  answeredResponse,
  disabled = false,
  errorMessage,
  isPending = false,
  onSubmit,
}: RequestInputCardProps) {
  const schema = request.response_schema;
  const type = schemaType(schema);
  const choices = schemaEnum(schema);
  const properties = useMemo(() => {
    if (
      type !== "object" ||
      !schema ||
      typeof schema.properties !== "object" ||
      schema.properties === null ||
      Array.isArray(schema.properties)
    ) {
      return [] as Array<[string, JsonSchema]>;
    }
    return Object.entries(schema.properties).filter(
      (entry): entry is [string, JsonSchema] =>
        typeof entry[1] === "object" &&
        entry[1] !== null &&
        !Array.isArray(entry[1]),
    );
  }, [schema, type]);
  const [scalarValue, setScalarValue] = useState<string | boolean>(
    type === "boolean" ? false : (choices[0] ?? ""),
  );
  const [objectValues, setObjectValues] = useState<
    Record<string, string | boolean>
  >(() =>
    Object.fromEntries(
      properties.map(([key, property]) => [
        key,
        property.type === "boolean" ? false : "",
      ]),
    ),
  );

  if (answered) {
    return (
      <article className="rounded-xl border border-green-200 bg-green-50 p-5">
        <div className="flex items-start gap-3">
          <CircleCheck
            aria-hidden="true"
            className="mt-0.5 size-5 shrink-0 text-green-700"
          />
          <div className="min-w-0">
            <h3 className="font-semibold text-green-950">Input provided</h3>
            <p className="mt-1 text-green-900 text-sm">{request.message}</p>
            <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-white/70 p-3 text-green-950 text-sm [overflow-wrap:anywhere]">
              {displayValue(answeredResponse)}
            </pre>
          </div>
        </div>
      </article>
    );
  }

  const submit = () => {
    if (type === "object") {
      onSubmit(
        Object.fromEntries(
          properties.map(([key, property]) => [
            key,
            coerceValue(objectValues[key] ?? "", property),
          ]),
        ),
      );
      return;
    }
    onSubmit(schema ? coerceValue(scalarValue, schema) : scalarValue);
  };
  const required = Array.isArray(schema?.required)
    ? schema.required.filter(
        (value): value is string => typeof value === "string",
      )
    : [];
  const objectIsValid = properties.every(
    ([key, property]) =>
      !required.includes(key) ||
      property.type === "boolean" ||
      String(objectValues[key] ?? "").trim().length > 0,
  );
  const scalarIsValid =
    type === "boolean" || String(scalarValue).trim().length > 0;

  return (
    <article className="rounded-xl border border-blue-200 bg-blue-50 p-5">
      <div className="flex items-start gap-3">
        <CircleHelp
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-blue-700"
        />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-blue-950">
            Agent needs your input
          </h3>
          <p className="mt-1 text-blue-900 text-sm">{request.message}</p>

          {request.payload !== null ? (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-medium text-blue-900">
                Review context
              </summary>
              <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-white/70 p-3 text-slate-800 [overflow-wrap:anywhere]">
                {displayValue(request.payload)}
              </pre>
            </details>
          ) : null}

          <div className="mt-4 space-y-3">
            {type === "object" ? (
              properties.map(([key, property]) => {
                const label =
                  typeof property.title === "string"
                    ? property.title
                    : key.replace(/_/g, " ");
                const propertyChoices = schemaEnum(property);
                return (
                  <div className="space-y-1.5" key={key}>
                    <Label htmlFor={`${request.interrupt_id}-${key}`}>
                      {label}
                      {required.includes(key) ? " *" : ""}
                    </Label>
                    {property.type === "boolean" ? (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={Boolean(objectValues[key])}
                          disabled={disabled || isPending}
                          id={`${request.interrupt_id}-${key}`}
                          onCheckedChange={(checked) =>
                            setObjectValues((current) => ({
                              ...current,
                              [key]: checked === true,
                            }))
                          }
                        />
                        <span className="text-slate-700 text-sm">
                          {objectValues[key] ? "Yes" : "No"}
                        </span>
                      </div>
                    ) : propertyChoices.length > 0 ? (
                      <Select
                        disabled={disabled || isPending}
                        onValueChange={(value) =>
                          setObjectValues((current) => ({
                            ...current,
                            [key]: value,
                          }))
                        }
                        value={String(objectValues[key] ?? "")}
                      >
                        <SelectTrigger
                          className="w-full bg-white"
                          id={`${request.interrupt_id}-${key}`}
                        >
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {propertyChoices.map((choice) => (
                            <SelectItem key={choice} value={choice}>
                              {formatSchemaChoice(choice, property)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="bg-white"
                        disabled={disabled || isPending}
                        id={`${request.interrupt_id}-${key}`}
                        onChange={(event) =>
                          setObjectValues((current) => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                        type={
                          property.type === "number" ||
                          property.type === "integer"
                            ? "number"
                            : "text"
                        }
                        value={String(objectValues[key] ?? "")}
                      />
                    )}
                  </div>
                );
              })
            ) : choices.length > 0 ? (
              <Select
                disabled={disabled || isPending}
                onValueChange={setScalarValue}
                value={String(scalarValue)}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {formatSchemaChoice(choice, schema)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : type === "boolean" ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={Boolean(scalarValue)}
                  disabled={disabled || isPending}
                  id={`${request.interrupt_id}-answer`}
                  onCheckedChange={(checked) =>
                    setScalarValue(checked === true)
                  }
                />
                <Label htmlFor={`${request.interrupt_id}-answer`}>
                  {scalarValue ? "Yes" : "No"}
                </Label>
              </div>
            ) : (
              <textarea
                aria-label="Your answer"
                className="min-h-28 w-full resize-y rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={disabled || isPending}
                maxLength={10_000}
                onChange={(event) => setScalarValue(event.target.value)}
                placeholder="Enter your answer"
                value={String(scalarValue)}
              />
            )}
          </div>

          {errorMessage ? (
            <p className="mt-3 text-destructive text-sm" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <Button
            className="mt-4"
            disabled={
              disabled ||
              isPending ||
              (type === "object" ? !objectIsValid : !scalarIsValid)
            }
            onClick={submit}
            type="button"
          >
            {isPending ? "Submitting…" : "Submit answer"}
          </Button>
        </div>
      </div>
    </article>
  );
}
